import React, { useState } from "react";
import { Layers, Instagram, Facebook, Linkedin, Globe, ArrowRight, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import PageBanner from "./PageBanner";

/* ───────────────────────── DATA (unchanged) ───────────────────────── */
const subunitsData = [
  {
    id: 1,
    name: "CS",
    fullName: "Computer Society",
    logo: "/asset/logos/cswh.png",
    bannerImage: "/asset/banners/csg.webp",
    description:
      "The IEEE Computer Society (CS) students branch chapter at isims is focused on advancing the fields of computing and technology. It provides students with opportunities to participate in workshops, coding competitions, and technical talks.",
    social: { instagram: "https://www.instagram.com/ieee.isims.cs/", facebook: "https://www.facebook.com/profile.php?id=100088090993586", linkedin: "https://www.linkedin.com/company/ieee-computer-society-chapter-isims-student-branch/posts/?feedView=all", website: "https://cs-isims.ieee.tn/" }
  },
  {
    id: 2,
    name: "IAS",
    fullName: "Industrial Applications Society",
    logo: "/asset/logos/iasjdid.png",
    bannerImage: "/asset/banners/iasg.webp",
    description:
      "The IEEE IAS students branch chapter at isims bridges the gap between students and the industry, focusing on applying engineering and scientific principles to solve industrial problems.",
    social: { instagram: "https://www.instagram.com/ieee_ias_isims/", facebook: "https://www.facebook.com/profile.php?id=61572324778458", linkedin: "https://www.linkedin.com/company/ieee-ias-isims-sbc/posts/?feedView=all", website: "https://ias-isims.ieee.tn/" }
  },
  {
    id: 3,
    name: "IES-PES",
    fullName: "Power & Energy Society",
    logo: "/asset/logos/iespeswh.png",
    bannerImage: "/asset/banners/iesg.webp",
    description:
      "The IEEE IES and PES students branch chapter at isims focuses on the application of electronics in industrial processes and the generation, transmission, and distribution of electrical energy.",
    social: { instagram: "https://www.instagram.com/ieee.ies.pes.isims.sb.jc/", facebook: "https://www.facebook.com/profile.php?id=61580047484275", linkedin: "https://www.linkedin.com/company/ieee-ies-pes-student-branch-chapter-%E2%80%93-isims/posts/?feedView=all", website: "https://ies-pes-isims.ieee.tn/" }
  },
  {
    id: 4,
    name: "WIE",
    fullName: "Women in Engineering",
    logo: "/asset/logos/wiejdid.png",
    bannerImage: "/asset/banners/wieg.webp",
    description:
      "The Women in Engineering (WIE) affinity group at isims focuses on promoting gender diversity in engineering and technology. It provides a platform for female engineers to connect, collaborate, and contribute.",
    social: { instagram: "https://www.instagram.com/ieee_wie_isims_sag/", facebook: "https://www.facebook.com/profile.php?id=100088426925351", linkedin: "https://www.linkedin.com/in/ieee-wie-isims-student-affinity-group-2a7a4035a/", website: "https://wie-isims.ieee.tn/" }
  },
  {
    id: 5,
    name: "SIGHT",
    fullName: "Humanitarian Technology",
    logo: "/asset/logos/sightwh.png",
    bannerImage: "/asset/banners/sightg.webp",
    description:
      "The IEEE SIGHT isims Student Branch Group aims to promote IEEE Humanitarian Technologies Activities and SIGHT goals within IEEE isims SB Community. Activities are mainly focused on sustainable SDG oriented technical projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.sight.group/", facebook: "https://www.facebook.com/profile.php?id=61584359810922", linkedin: "#", website: "#" }
  },
  {
    id: 6,
    name: "SMC",
    fullName: "Systems, Man & Cybernetics",
    logo: "/asset/logos/smcwh.png",
    bannerImage: "/asset/banners/smcg.webp",
    description:
      "The IEEE SMC students branch chapter at isims focuses on the interdisciplinary study of systems engineering, human-machine interaction, and intelligent systems.",
    social: { instagram: "https://www.instagram.com/ieee_smc_isims_sb_chapter/", facebook: "https://www.facebook.com/profile.php?id=61557150934003", linkedin: "https://www.linkedin.com/search/results/all/?keywords=smc%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://smc-isims.ieee.tn/" }
  },
  {
    id: 7,
    name: "RAS",
    fullName: "Robotics & Automation",
    logo: "/asset/logos/raswh.png",
    bannerImage: "/asset/banners/rasg.webp",
    description:
      "The Robotics and Automation Society (RAS) students branch chapter at isims is a hub for students interested in the fields of robotics, automation, and artificial intelligence.",
    social: { instagram: "https://www.instagram.com/ieee_ras_chapter_isims_sb/", facebook: "https://www.facebook.com/profile.php?id=100090530634047", linkedin: "linkedin.com/search/results/all/?keywords=ieee%20ras%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://ras-isims.ieee.tn/" }
  },
];

const EASE = "ease-[cubic-bezier(0.16,1,0.3,1)]";
const pad = (n) => String(n).padStart(2, "0");

/* ───────────────────────── SOCIAL ICON ───────────────────────── */
function SocialIcon({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/25 hover:bg-white hover:text-[#00334d] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc]"
    >
      {React.cloneElement(icon, { className: "w-4 h-4" })}
    </a>
  );
}

/* ───────────────────────── ONE PANEL ─────────────────────────
   Desktop: a row of tall panels, the open one grows and the rest fold into slim strips.
   Phone:   the same panels stacked, the open one grows downward.            */
function Panel({ unit, index, on, onOpen }) {
  const { social } = unit;
  const links = [
    ["instagram", "Instagram", <Instagram />],
    ["facebook", "Facebook", <Facebook />],
    ["linkedin", "LinkedIn", <Linkedin />],
    ["website", "Website", <Globe />],
  ].filter(([k]) => social[k] && social[k] !== "#");

  return (
    <article
      onMouseEnter={onOpen}
      style={{ flexGrow: on ? 8 : 1 }}
      className={`relative overflow-hidden bg-[#00334d] min-w-0 lg:basis-0 lg:h-full transition-[height,flex-grow] duration-[800ms] ${EASE} ${
        on ? "h-[560px] sm:h-[520px]" : "h-[84px]"
      }`}
    >
      {/* photo */}
      <img
        src={unit.bannerImage}
        alt=""
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ${EASE} ${
          on ? "opacity-70 scale-100" : "opacity-25 scale-110 grayscale"
        }`}
      />
      {/* blueprint grid */}
      <div
        className="absolute inset-0 opacity-[0.1] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className={`absolute inset-0 bg-gradient-to-t from-[#00334d] via-[#00334d]/60 to-[#00334d]/10 transition-opacity duration-700 ${
          on ? "opacity-100" : "opacity-60"
        }`}
      />

      {/* corner brackets, open panel only */}
      <span className={`absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#7dd3fc] z-10 pointer-events-none transition-opacity duration-500 ${on ? "opacity-100 delay-500" : "opacity-0"}`} />
      <span className={`absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#7dd3fc] z-10 pointer-events-none transition-opacity duration-500 ${on ? "opacity-100 delay-500" : "opacity-0"}`} />

      {/* ── closed state ── */}
      <div
        className={`absolute inset-0 z-[5] flex lg:flex-col items-center gap-4 lg:gap-0 lg:justify-between px-5 lg:px-0 lg:py-7 pointer-events-none transition-opacity duration-300 ${
          on ? "opacity-0" : "opacity-100 delay-300"
        }`}
        aria-hidden="true"
      >
        <span className="text-sm font-black text-[#7dd3fc] tabular-nums w-7 lg:w-auto">{pad(index + 1)}</span>
        <span
          className="text-3xl lg:text-5xl font-black leading-none tracking-tighter text-transparent uppercase lg:[writing-mode:vertical-rl] lg:rotate-180 lg:flex-1 lg:flex lg:items-center"
          style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.85)" }}
        >
          {unit.name}
        </span>
        <span className="ml-auto lg:ml-0 w-9 h-9 flex items-center justify-center border border-white/30 text-white">
          <Plus className="w-4 h-4" />
        </span>
      </div>

      {/* click target for closed panels (a real button so it is keyboard friendly) */}
      {!on && (
        <button
          onClick={onOpen}
          onFocus={onOpen}
          aria-label={`Open ${unit.name}: ${unit.fullName}`}
          className="absolute inset-0 z-20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7dd3fc]"
        />
      )}

      {/* ── open state ──  fixed inner width on desktop so text never reflows while the panel grows */}
      <div
        className={`absolute inset-0 z-10 transition-[opacity,visibility] duration-500 ${
          on ? "visible opacity-100 delay-[350ms]" : "invisible opacity-0"
        }`}
      >
        <div className="relative h-full w-full lg:w-[min(760px,54vw)] flex flex-col justify-between p-6 sm:p-8 lg:p-12">
          {/* top: logo + giant outlined index */}
          <div className="flex items-start justify-between">
            <img
              src={unit.logo}
              alt={`${unit.name} logo`}
              className="w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44 object-contain brightness-0 invert drop-shadow-2xl"
            />
            <span
              className="text-6xl sm:text-7xl lg:text-8xl font-black leading-none tracking-tighter text-transparent tabular-nums select-none"
              style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            >
              {pad(index + 1)}
            </span>
          </div>

          {/* bottom: name, text, actions */}
          <div>
            <h3 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white uppercase leading-[0.95] tracking-tighter">
              {unit.name}
            </h3>
            <p className="mt-2 text-sm sm:text-base font-bold text-[#7dd3fc]">{unit.fullName}</p>
            <p className="mt-4 text-sm sm:text-[15px] text-white/80 leading-relaxed max-w-[56ch]">{unit.description}</p>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div className="flex items-center gap-2">
                {links.map(([k, label, icon]) => (
                  <SocialIcon key={k} href={social[k]} icon={icon} label={`${unit.name} ${label}`} />
                ))}
              </div>
              <Link
                to={`/${unit.name.toLowerCase()}register`}
                className="group/btn inline-flex items-center justify-center gap-2 h-11 px-6 bg-white text-[#00334d] text-sm font-bold hover:bg-[#7dd3fc] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc] focus-visible:ring-offset-2 focus-visible:ring-offset-[#00334d]"
              >
                Register now
                <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ───────────────────────── PAGE ───────────────────────── */
export default function SubunitsPage() {
  const [activeId, setActiveId] = useState(subunitsData[0].id);

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-[#fafcff]">
        <PageBanner
          title="Our Subunits"
          description="Explore the technical chapters and affinity groups within IEEE ISIMS Student Branch, each driving innovation in their specialized fields."
          Icon={Layers}
        />

        <div id="subunits-list" className="relative bg-[#fafcff] w-full py-14 md:py-20 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                repeating-linear-gradient(-45deg, rgba(15,105,157,0.05) 0px, rgba(15,105,157,0.05) 1px, transparent 1px, transparent 18px),
                repeating-linear-gradient(45deg, rgba(15,105,157,0.04) 0px, rgba(15,105,157,0.04) 1px, transparent 1px, transparent 28px)
              `,
            }}
          />

          <div className="relative w-[90%] max-w-[1400px] mx-auto">
            {/* header */}
            <div className="flex items-end gap-5 mb-10 md:mb-14">
              <span
                className="text-[84px] sm:text-[110px] leading-[0.8] font-black tracking-tighter text-transparent select-none"
                style={{ WebkitTextStroke: "2px #006699" }}
                aria-hidden="true"
              >
                {pad(subunitsData.length)}
              </span>
              <div className="pb-1">
                <h2 className="text-xl sm:text-2xl font-bold text-[#00334d] leading-tight">Chapters and groups</h2>
                <p className="text-sm text-slate-500 mt-1">Hover or tap one to open it, then register.</p>
              </div>
            </div>

            {/* the row */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col lg:flex-row lg:h-[640px] gap-px bg-[#006699]/25 p-px shadow-xl"
            >
              {subunitsData.map((unit, i) => (
                <Panel
                  key={unit.id}
                  unit={unit}
                  index={i}
                  on={unit.id === activeId}
                  onOpen={() => setActiveId(unit.id)}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </MotionConfig>
  );
}